import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Divider, FormControl, InputLabel, MenuItem, Select, TablePagination, Tooltip } from "@mui/material";
import { IconPlus, IconUpload } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import { AddButton } from "components/ui/buttons/StyledButtons";
import AttendanceTable from "components/attendances/AttendanceTable";
import AttendancesFilters from "components/attendances/AttendancesFilters";
import AttendanceFormModal from "components/attendances/AttendanceFormModal";
import AttendanceDetailModal from "components/attendances/AttendanceDetailModal";
import AttendanceImportModal from "components/attendances/AttendanceImportModal";
import useContractPermissions from "hooks/useContractPermissions";
import useDragScroll from "hooks/useDragScroll";
import { ATTENDANCE_STATUSES } from "utils/attendance";

const EMPTY_FILTERS = {
  customer: null,
  customer_cpf: "",
  attendant_id: "",
  created_at_start: "",
  created_at_end: "",
  subject: "",
  status: "",
  process_number: "",
};

const AttendancesList = () => {
  const {
    canViewAttendances,
    canCreateAttendances,
    canAddAttendanceInteractions,
    canImportData,
    loading: permissionsLoading,
  } = useContractPermissions();
  const {
    ref: tableScrollRef,
    isDragging: isTableDragging,
    isSelectionMode: isTableSelectionMode,
    onMouseDown: handleTableScrollMouseDown,
  } = useDragScroll({ verticalScroll: "window" });
  const [attendances, setAttendances] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [importedStatusFilter, setImportedStatusFilter] = useState("all");
  const [subjects, setSubjects] = useState([]);
  const [statuses, setStatuses] = useState(ATTENDANCE_STATUSES);
  const [users, setUsers] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");

  const fetchAttendances = useCallback(() => {
    const params = new URLSearchParams({
      page: String(page + 1),
      page_size: String(rowsPerPage),
      ordering: "-last_interaction_at",
    });
    if (searchTerm.trim()) params.set("contains", searchTerm.trim());
    if (filters.customer?.id) params.set("customer_id", String(filters.customer.id));
    if (filters.customer?.name && !filters.customer?.id) params.set("customer_name", filters.customer.name);
    if (filters.customer_cpf) params.set("customer_cpf", filters.customer_cpf);
    if (filters.attendant_id) params.set("attendant_id", String(filters.attendant_id));
    if (filters.created_at_start) params.set("created_at_start", filters.created_at_start);
    if (filters.created_at_end) params.set("created_at_end", filters.created_at_end);
    if (filters.subject) params.set("subject", filters.subject);
    if (filters.status) params.set("status", filters.status);
    if (filters.process_number) params.set("process_number", filters.process_number);
    if (importedStatusFilter !== "all") params.set("imported_status", importedStatusFilter);

    fetch(`/backend/attendances/?${params.toString()}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || "Não foi possível carregar os atendimentos.");
        }
        return data;
      })
      .then((data) => {
        setAttendances(data.results || []);
        setTotalRecords(data.count || 0);
        setError("");
      })
      .catch((err) => {
        setError(err.message);
        setAttendances([]);
        setTotalRecords(0);
      });
  }, [page, rowsPerPage, searchTerm, filters, importedStatusFilter]);

  useEffect(() => {
    if (!permissionsLoading && canViewAttendances) {
      fetchAttendances();
    }
  }, [fetchAttendances, permissionsLoading, canViewAttendances, reloadKey]);

  useEffect(() => {
    fetch("/backend/attendances/subjects/")
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setSubjects(Array.isArray(data) ? data : []))
      .catch(() => setSubjects([]));
    fetch("/backend/attendances/statuses/")
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setStatuses(Array.isArray(data) ? data : []))
      .catch(() => setStatuses([]));
    fetch("/backend/users-list/")
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, []);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSaved = (payload) => {
    setOpenCreate(false);
    setPage(0);
    fetchAttendances();
    if (payload?.id) setSelectedId(payload.id);
  };

  const labelDisplayedRows = ({ from, to, count }) => `${from}–${to} de ${count}`;

  const pagination = (
    <TablePagination
      rowsPerPageOptions={[5, 10, 25, 50, 100, 150]}
      component="div"
      count={totalRecords}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      labelRowsPerPage="Atendimentos por página"
      labelDisplayedRows={labelDisplayedRows}
    />
  );

  if (!permissionsLoading && !canViewAttendances) {
    return (
      <MainCard title="Cadastro de atendimentos">
        <Alert severity="warning">Você não tem permissão para visualizar atendimentos.</Alert>
      </MainCard>
    );
  }

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de atendimentos"
        actions={[
          ...(canImportData
            ? [
                <Tooltip key="import-xlsx" title="Importar atendimentos via planilha Excel">
                  <AddButton onClick={() => setOpenImport(true)}>
                    <IconUpload stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
                    Importar planilha
                  </AddButton>
                </Tooltip>,
              ]
            : []),
          ...(canCreateAttendances
            ? [
                <AddButton key="new" onClick={() => setOpenCreate(true)}>
                  <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
                  Novo atendimento
                </AddButton>,
              ]
            : []),
        ]}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <SearchSection
            value={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setPage(0);
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="attendances-imported-status-filter-label">Status importado</InputLabel>
            <Select
              labelId="attendances-imported-status-filter-label"
              id="attendances-imported-status-filter"
              value={importedStatusFilter}
              label="Status importado"
              onChange={(e) => {
                setImportedStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="imported">Somente importados</MenuItem>
              <MenuItem value="not_imported">Somente não importados</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 2 }} />
        <AttendancesFilters
          filters={filters}
          subjects={subjects}
          statuses={statuses}
          users={users}
          onFiltersChange={(next) => {
            setFilters(next);
            setPage(0);
          }}
          onClearFilters={() => {
            setFilters(EMPTY_FILTERS);
            setPage(0);
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1, mb: 1 }}>
          {attendances.length > 0 && (
            <Box sx={{ marginLeft: "auto" }}>
              {pagination}
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
          ref={tableScrollRef}
          onMouseDown={handleTableScrollMouseDown}
          sx={{
            overflowX: "auto",
            cursor: isTableDragging
              ? "grabbing"
              : isTableSelectionMode
                ? "text"
                : "grab",
            userSelect: isTableSelectionMode ? "text" : "none",
            WebkitUserSelect: isTableSelectionMode ? "text" : "none",
            "& button:not(.customer-name-button), & input, & a, & .MuiCheckbox-root": {
              cursor: "pointer",
              userSelect: "none",
              WebkitUserSelect: "none",
            },
            "& .customer-name-button": {
              cursor: isTableSelectionMode ? "text" : "pointer",
              userSelect: isTableSelectionMode ? "text" : "none",
              WebkitUserSelect: isTableSelectionMode ? "text" : "none",
            },
          }}
        >
          <AttendanceTable
            attendances={attendances}
            selectedId={selectedId}
            onView={(item) => setSelectedId(item.id)}
          />
        </Box>
      </MainCard>

      {attendances.length > 0 && pagination}

      <AttendanceImportModal
        open={openImport}
        onClose={() => setOpenImport(false)}
        onImported={() => {
          setImportedStatusFilter("imported");
          setPage(0);
          setReloadKey((value) => value + 1);
        }}
      />
      <AttendanceFormModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={handleSaved}
        subjects={subjects}
      />
      <AttendanceDetailModal
        open={Boolean(selectedId)}
        attendanceId={selectedId}
        onClose={() => setSelectedId(null)}
        canAddInteraction={canAddAttendanceInteractions}
        onAttendanceUpdated={() => fetchAttendances()}
      />
    </div>
  );
};

export default AttendancesList;
